import { useState } from 'react';
import type { FormEvent } from 'react';
import { showSuccess, showError } from '@/utils/toastHelpers';
import { useConfirmRegistration, useDeleteRegistration, useUpdateRegistrationBibNumber, useCmsCreateRegistration, useUpdateRegistration } from '@/hooks/useRegistrations';
import { normalizeBibNumberingConfig } from '@/utils/bibNumbering';

const emptyRegistrationForm = () => ({
  firstName: '', lastName: '', email: '', phone: '', city: '', country: '',
  gender: '', dateOfBirth: '', participantType: '', teamName: '', tshirtSize: '',
  discountCode: '', emergencyContactPhone: '', cookiesConsent: false, privacyPolicy: false,
  raceId: '', raceTypeId: '', registrationStatus: 'Pending', paymentStatus: 'Pending',
});

export const useRegistrationsActions = (raceTypeDetails: Record<string, any>) => {
  const confirmRegistration = useConfirmRegistration();
  const deleteRegistration = useDeleteRegistration();
  const updateRegistrationBibNumber = useUpdateRegistrationBibNumber();
  const cmsCreateRegistration = useCmsCreateRegistration();
  const updateRegistration = useUpdateRegistration();

  const [selectedRegistration, setSelectedRegistration] = useState<any>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmingRegistration, setConfirmingRegistration] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isBibDialogOpen, setIsBibDialogOpen] = useState(false);
  const [editingBibRegistration, setEditingBibRegistration] = useState<any>(null);
  const [bibNumberInput, setBibNumberInput] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState<any>(null);
  const [registrationFormData, setRegistrationFormData] = useState<any>(emptyRegistrationForm());

  const syncRegistrationState = (updatedRegistration: any) => {
    setSelectedRegistration((current: any) => current?.id === updatedRegistration.id ? updatedRegistration : current);
    setConfirmingRegistration((current: any) => current?.id === updatedRegistration.id ? updatedRegistration : current);
    setEditingBibRegistration((current: any) => current?.id === updatedRegistration.id ? updatedRegistration : current);
  };

  const handleViewDetails = (registration: any) => {
    setEditingRegistration(null);
    setRegistrationFormData(emptyRegistrationForm());
    setIsEditDialogOpen(false);
    setSelectedRegistration(registration);
    setIsDetailDialogOpen(true);
  };

  const handleOpenConfirmDialog = (registration: any) => {
    setConfirmingRegistration(registration);
    setIsConfirmDialogOpen(true);
  };

  const handleOpenBibDialog = (registration: any) => {
    setEditingBibRegistration(registration);
    setBibNumberInput(registration?.bibNumber != null ? String(registration.bibNumber) : '');
    setIsBibDialogOpen(true);
  };

  const handleConfirm = async (paymentStatus: string) => {
    if (!confirmingRegistration) return;

    try {
      const updated: any = await confirmRegistration.mutateAsync({ id: confirmingRegistration.id, paymentStatus });
      const numbering = normalizeBibNumberingConfig(raceTypeDetails[updated.raceTypeId]);

      syncRegistrationState(updated);
      setIsConfirmDialogOpen(false);
      setConfirmingRegistration(null);

      const successMessage = !numbering.bibNumberingEnabled
        ? 'Регистрацијата е потврдена без стартен број. Е-пошта за потврда е испратена.'
        : updated.bibNumber != null
          ? 'Регистрацијата е потврдена. Стартен број: #' + updated.bibNumber + '. Е-пошта за потврда е испратена.'
          : 'Регистрацијата е потврдена. Е-пошта за потврда е испратена.';

      showSuccess('Успешно потврдено', successMessage);
    } catch (error) {
      showError(error, 'регистрацијата');
    }
  };

  const handleSaveBibNumber = async () => {
    if (!editingBibRegistration) return;

    const trimmedValue = bibNumberInput.trim();
    if (trimmedValue !== '' && !/^\d+$/.test(trimmedValue)) {
      showError({ message: 'Внесете валиден цел број или оставете празно за бришење.' });
      return;
    }

    try {
      const updated: any = await updateRegistrationBibNumber.mutateAsync({
        id: editingBibRegistration.id,
        bibNumber: trimmedValue === '' ? null : Number(trimmedValue),
      });

      syncRegistrationState(updated);
      setIsBibDialogOpen(false);
      setBibNumberInput(updated.bibNumber != null ? String(updated.bibNumber) : '');

      showSuccess(
        'Стартниот број е ажуриран',
        updated.bibNumber != null
          ? 'Новиот стартен број е #' + updated.bibNumber + '.'
          : 'Стартниот број е исчистен за оваа регистрација.'
      );
    } catch (error) {
      showError(error, 'стартниот број');
    }
  };

  const handleDelete = (id: any) => setDeleteTarget(id);

  const handleOpenCreate = () => {
    setIsDetailDialogOpen(false);
    setSelectedRegistration(null);
    setEditingRegistration(null);
    setRegistrationFormData(emptyRegistrationForm());
    setIsEditDialogOpen(true);
  };

  const handleOpenEdit = (registration: any) => {
    setIsDetailDialogOpen(false);
    setSelectedRegistration(null);
    setEditingRegistration(registration);
    setRegistrationFormData({
      firstName: registration.firstName || '',
      lastName: registration.lastName || '',
      email: registration.email || '',
      phone: registration.phone || '',
      city: registration.city || '',
      country: registration.country || '',
      gender: registration.gender || '',
      dateOfBirth: registration.dateOfBirth ? registration.dateOfBirth.split('T')[0] : '',
      participantType: registration.participantType || '',
      teamName: registration.teamName || '',
      tshirtSize: registration.tshirtSize || '',
      discountCode: registration.discountCode || '',
      emergencyContactPhone: registration.emergencyContactPhone || '',
      cookiesConsent: !!registration.cookiesConsent,
      privacyPolicy: !!registration.privacyPolicy,
      raceId: registration.raceId ? registration.raceId.toString() : '',
      raceTypeId: registration.raceTypeId ? registration.raceTypeId.toString() : '',
      registrationStatus: registration.registrationStatus || 'Pending',
      paymentStatus: registration.paymentStatus || 'Pending',
    });
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingRegistration(null);
    setRegistrationFormData(emptyRegistrationForm());
  };

  const handleSubmitRegistration = async (e: FormEvent) => {
    e.preventDefault();
    if (!registrationFormData.dateOfBirth) {
      showError({ message: 'Внесете датум на раѓање.' });
      return;
    }
    try {
      const payload = {
        firstName: registrationFormData.firstName,
        lastName: registrationFormData.lastName,
        email: registrationFormData.email,
        phone: registrationFormData.phone || null,
        city: registrationFormData.city || null,
        country: registrationFormData.country || null,
        gender: registrationFormData.gender || null,
        dateOfBirth: new Date(registrationFormData.dateOfBirth).toISOString(),
        participantType: registrationFormData.participantType || null,
        teamName: registrationFormData.teamName || null,
        tshirtSize: registrationFormData.tshirtSize || null,
        discountCode: registrationFormData.discountCode || null,
        emergencyContactPhone: registrationFormData.emergencyContactPhone || null,
        cookiesConsent: registrationFormData.cookiesConsent,
        privacyPolicy: registrationFormData.privacyPolicy,
        raceId: registrationFormData.raceId,
        raceTypeId: parseInt(registrationFormData.raceTypeId, 10),
        registrationStatus: registrationFormData.registrationStatus,
        paymentStatus: registrationFormData.paymentStatus,
      };

      if (editingRegistration) {
        await updateRegistration.mutateAsync({ id: editingRegistration.id, data: payload });
        showSuccess('Успешно ажурирано', 'Регистрацијата е успешно ажурирана.');
      } else {
        await cmsCreateRegistration.mutateAsync(payload);
        showSuccess('Успешно додадено', 'Нова регистрација е успешно додадена.');
      }
      setIsEditDialogOpen(false);
      setEditingRegistration(null);
      setRegistrationFormData(emptyRegistrationForm());
    } catch (error) {
      showError(error, 'регистрацијата');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRegistration.mutateAsync(deleteTarget);
      showSuccess('Успешно избришано', 'Регистрацијата е успешно избришана.');
    } catch (error) {
      showError(error, 'регистрацијата');
    } finally {
      setDeleteTarget(null);
    }
  };

  return {
    selectedRegistration,
    setSelectedRegistration,
    isDetailDialogOpen,
    setIsDetailDialogOpen,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    confirmingRegistration,
    deleteTarget,
    setDeleteTarget,
    isBibDialogOpen,
    setIsBibDialogOpen,
    editingBibRegistration,
    bibNumberInput,
    setBibNumberInput,
    confirmRegistrationPending: confirmRegistration.isPending,
    updateBibNumberPending: updateRegistrationBibNumber.isPending,
    handleViewDetails,
    handleOpenConfirmDialog,
    handleOpenBibDialog,
    handleConfirm,
    handleSaveBibNumber,
    handleDelete,
    confirmDelete,
    isEditDialogOpen,
    setIsEditDialogOpen,
    editingRegistration,
    registrationFormData,
    setRegistrationFormData,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseEditDialog,
    handleSubmitRegistration,
    registrationSubmitPending: cmsCreateRegistration.isPending || updateRegistration.isPending,
  };
};
